import { DEFAULT_PRESET_ID } from '../constants/presets';
import PresetStorage from './PresetStorage';

/**
 * PresetManager
 *
 * Gerencia estado e operacoes de presets.
 * Singleton para manter estado consistente em toda a aplicacao.
 */
class PresetManager {
    constructor() {
        this.storage = new PresetStorage();
        this.presets = new Map();
        this.systemPresets = new Map();
        this.customPresets = new Map();
        this.activePresetId = null;
        this.listeners = new Set();
    }

    /**
     * Inicializa o manager carregando presets
     * @returns {Promise<void>}
     */
    async initialize() {
        await this.loadPresets();

        // Carregar preset ativo ou usar padrao
        const savedActiveId = this.storage.getActivePresetId();

        if (savedActiveId && this.presets.has(savedActiveId)) {
            this.activePresetId = savedActiveId;
        } else {
            this.activePresetId = DEFAULT_PRESET_ID;
            this.storage.setActivePresetId(DEFAULT_PRESET_ID);
        }
    }

    /**
     * Carrega todos os presets do storage
     * @returns {Promise<void>}
     */
    async loadPresets() {
        try {
            const { system, custom } = await this.storage.fetchPresets();

            // Limpar maps
            this.presets.clear();
            this.systemPresets.clear();
            this.customPresets.clear();

            // Adicionar presets do sistema
            system.forEach((preset) => {
                this.presets.set(preset.id, preset);
                this.systemPresets.set(preset.id, preset);
            });

            // Adicionar presets customizados
            custom.forEach((preset) => {
                this.presets.set(preset.id, preset);
                this.customPresets.set(preset.id, preset);
            });

            this.notifyListeners();
        } catch (error) {
            console.error('Erro ao carregar presets:', error);
            throw error;
        }
    }

    /**
     * Retorna todos os presets
     * @returns {Array} Array de presets
     */
    getPresets() {
        return Array.from(this.presets.values());
    }

    /**
     * Retorna presets do sistema
     * @returns {Array} Array de presets do sistema
     */
    getSystemPresets() {
        return Array.from(this.systemPresets.values());
    }

    /**
     * Retorna presets customizados
     * @returns {Array} Array de presets customizados
     */
    getCustomPresets() {
        return Array.from(this.customPresets.values());
    }

    /**
     * Retorna o preset ativo
     * @returns {Object|null} Preset ativo
     */
    getActivePreset() {
        if (!this.activePresetId) {
            return null;
        }
        return this.presets.get(this.activePresetId) || null;
    }

    /**
     * Retorna o ID do preset ativo
     * @returns {string|null}
     */
    getActivePresetId() {
        return this.activePresetId;
    }

    /**
     * Define o preset ativo
     * @param {string} id - ID do preset
     */
    setActivePreset(id) {
        if (!this.presets.has(id)) {
            throw new Error('Preset nao encontrado');
        }

        this.activePresetId = id;
        this.storage.setActivePresetId(id);
        this.notifyListeners();
    }

    /**
     * Cria um novo preset customizado
     * @param {Object} presetData - Dados do preset
     * @returns {Promise<Object>} Preset criado
     */
    async createPreset(presetData) {
        // Validacoes
        this.validatePresetData(presetData);

        // Verificar nome duplicado
        const existingNames = this.getCustomPresets().map((p) => p.name.toLowerCase());
        if (existingNames.includes(presetData.name.toLowerCase())) {
            throw new Error('Ja existe um preset com este nome');
        }

        try {
            const newPreset = await this.storage.savePreset(presetData);

            // Adicionar aos maps
            this.presets.set(newPreset.id, newPreset);
            this.customPresets.set(newPreset.id, newPreset);

            // Aplicar automaticamente o novo preset
            this.setActivePreset(newPreset.id);

            this.notifyListeners();

            return newPreset;
        } catch (error) {
            console.error('Erro ao criar preset:', error);
            throw error;
        }
    }

    /**
     * Atualiza um preset customizado
     * @param {string} id - ID do preset
     * @param {Object} updates - Atualizacoes parciais
     * @returns {Promise<Object>} Preset atualizado
     */
    async updatePreset(id, updates) {
        // Verificar se e preset do sistema
        if (this.systemPresets.has(id)) {
            throw new Error('Presets do sistema nao podem ser editados');
        }

        if (!this.customPresets.has(id)) {
            throw new Error('Preset nao encontrado');
        }

        // Validar atualizacoes
        if (updates.name || updates.columns) {
            this.validatePresetData({
                name: updates.name || this.presets.get(id).name,
                columns: updates.columns || this.presets.get(id).columns,
            });
        }

        // Verificar nome duplicado (exceto o proprio preset)
        if (updates.name) {
            const existingNames = this.getCustomPresets()
                .filter((p) => p.id !== id)
                .map((p) => p.name.toLowerCase());

            if (existingNames.includes(updates.name.toLowerCase())) {
                throw new Error('Ja existe um preset com este nome');
            }
        }

        try {
            const updatedPreset = await this.storage.updatePreset(id, updates);

            // Atualizar maps
            this.presets.set(id, updatedPreset);
            this.customPresets.set(id, updatedPreset);

            // Se e o preset ativo, notificar mudanca
            if (this.activePresetId === id) {
                this.notifyListeners();
            }

            return updatedPreset;
        } catch (error) {
            console.error('Erro ao atualizar preset:', error);
            throw error;
        }
    }

    /**
     * Exclui um preset customizado
     * @param {string} id - ID do preset
     * @returns {Promise<void>}
     */
    async deletePreset(id) {
        // Verificar se e preset do sistema
        if (this.systemPresets.has(id)) {
            throw new Error('Presets do sistema nao podem ser excluidos');
        }

        if (!this.customPresets.has(id)) {
            throw new Error('Preset nao encontrado');
        }

        try {
            await this.storage.deletePreset(id);

            // Remover dos maps
            this.presets.delete(id);
            this.customPresets.delete(id);

            // Se era o preset ativo, aplicar preset padrao
            if (this.activePresetId === id) {
                this.setActivePreset(DEFAULT_PRESET_ID);
            }

            this.notifyListeners();
        } catch (error) {
            console.error('Erro ao excluir preset:', error);
            throw error;
        }
    }

    /**
     * Valida dados de um preset
     * @param {Object} presetData
     * @throws {Error} Se dados invalidos
     */
    validatePresetData(presetData) {
        // Validar nome
        if (!presetData.name || typeof presetData.name !== 'string') {
            throw new Error('Nome do preset e obrigatorio');
        }

        if (presetData.name.trim().length === 0) {
            throw new Error('Nome do preset nao pode estar vazio');
        }

        if (presetData.name.length > 100) {
            throw new Error('Nome do preset muito longo (maximo 100 caracteres)');
        }

        // Validar colunas
        if (!presetData.columns || !Array.isArray(presetData.columns)) {
            throw new Error('Colunas do preset sao obrigatorias');
        }

        if (presetData.columns.length === 0) {
            throw new Error('Preset deve ter pelo menos uma coluna');
        }

        if (presetData.columns.length > 30) {
            throw new Error('Preset nao pode ter mais de 30 colunas');
        }

        // Validar estrutura das colunas
        presetData.columns.forEach((col, index) => {
            if (!col.type || (col.type !== 'predefined' && col.type !== 'custom')) {
                throw new Error(`Coluna ${index + 1}: tipo invalido`);
            }

            if (col.type === 'predefined' && !col.key) {
                throw new Error(`Coluna ${index + 1}: chave obrigatoria para coluna pre-definida`);
            }

            if (col.type === 'custom') {
                if (!col.name || col.name.trim().length === 0) {
                    throw new Error(`Coluna ${index + 1}: nome obrigatorio para coluna customizada`);
                }

                if (!col.expression || col.expression.trim().length === 0) {
                    throw new Error(`Coluna ${index + 1}: expressao obrigatoria para coluna customizada`);
                }

                if (col.expression.length > 500) {
                    throw new Error(`Coluna ${index + 1}: expressao muito longa (maximo 500 caracteres)`);
                }
            }
        });
    }

    /**
     * Adiciona um listener para mudancas
     * @param {Function} callback
     */
    addListener(callback) {
        this.listeners.add(callback);
    }

    /**
     * Remove um listener
     * @param {Function} callback
     */
    removeListener(callback) {
        this.listeners.delete(callback);
    }

    /**
     * Notifica todos os listeners sobre mudancas
     */
    notifyListeners() {
        this.listeners.forEach((callback) => {
            try {
                callback();
            } catch (error) {
                console.error('Erro ao notificar listener:', error);
            }
        });
    }
}

// Singleton instance
const presetManager = new PresetManager();

export default presetManager;
